import { useCallback, useContext, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useMediaQuery } from 'react-responsive';
import { Box, Modal, Typography, Grid, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiValidSelect from 'src/components/input/MuiValidSelect';
import MuiValidTextField from 'src/components/input/MuiValidTextField';
import { useAPI } from 'src/services/api';
import ToastContext from 'src/store/toast-context';
import { States } from 'src/util/states';
import RemoveModal from 'src/components/modal/RemoveModal';
import formatPhoneNumber from 'src/util/formatPhoneNumber';
import ContactSchema from 'src/components/schemas/contactSchema';
import ModalStyle from 'src/components/modal/ModalStyle';
import AuthContext from 'src/store/auth-context';
import FormObserver from 'src/components/FormObserver';
import useFetchAddressByZip from 'src/services/useFetchAddressByZip';

import BusinessContext from 'src/store/business-context';

const ContactModal = () => {
  const { handleClose, initialValues, open, updating, setContacts, contacts } =
    useContext(BusinessContext);
  const api = useAPI();
  const { handleError, success, error } = useContext(ToastContext);
  const [changed, setChanged] = useState(false);
  const { backdoor } = useContext(AuthContext);
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });
  const fetchAddressByZip = useFetchAddressByZip();

  const updateRow = (values, args) => {
    setContacts((prevState) => {
      return prevState.map((row) => {
        if (row.recno === '-New-' && row.ownerRecno === updating.ownerRecno) {
          return { ...row, ...values, ...args };
        }
        if (row.recno === updating.recno) {
          return { ...row, ...values, ...args };
        }
        return row;
      });
    });
  };

  const removeNewContact = async () => {
    await api.delete(`/business/contact/delete/${updating.recno}`);
    setContacts(contacts.filter((contact) => contact.recno !== updating.recno));
  };

  const removeOldContact = async () => {
    const { data } = await api.patch(`/business/contact/markDelete/${updating.recno}`, updating);
    updateRow(updating, { addDel: 'D', recno: data.recno });
  };

  const updateExistingContact = async (values) => {
    if (updating.addDel === 'O') {
      const { data } = await api.patch(`/business/contact/${updating.recno}`, {
        ...values,
        ownerRecno: updating.recno,
        addDel: updating.addDel,
      });
      updateRow(values, { addDel: 'C', recno: data.recno });
    } else {
      updateRow(values);
      await api.patch(`/business/contact/${updating.recno}`, {
        ...values,
        ownerRecno: updating.ownerRecno,
      });
    }
  };

  const addNewContact = async (values) => {
    const { data } = await api.post('/business/contact', values);
    setContacts([{ ...values, recno: data.recno, addDel: 'A' }].concat(contacts));
  };

  const handleRemove = async () => {
    handleClose();
    try {
      if (updating.addDel === 'A') {
        await removeNewContact();
        success('Successfully removed contact');
      }
      if (updating.addDel === 'D') {
        error(`Contact has already been marked for deletion`);
      }
      if (updating.addDel === 'O' || updating.addDel === 'C') {
        await removeOldContact();
        success('Successfully marked contact for deletion');
      }
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  const handleSubmit = async (values) => {
    handleClose();
    try {
      if (updating) {
        await updateExistingContact(values);
        success('Succesfully updated contact');
      } else {
        await addNewContact(values);
        success('Successfully added contact');
      }
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  const handleOnChange = (e) => {
    if (!changed && !(e.target.value === updating[e.target.name].toString())) setChanged(true);
  };

  const handleZipChange = useCallback(
    async (event, setFieldValue) => {
      const address = await fetchAddressByZip(event.target.value);
      if (address) {
        setFieldValue('addrState', address.state);
        setFieldValue('addrCity', address.city);
      }
    },
    [fetchAddressByZip]
  );

  return (
    <>
      <Formik
        onSubmit={(values, { resetForm }) => {
          setChanged(false);
          resetForm();
          handleSubmit(values);
        }}
        validationSchema={ContactSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ resetForm, setFieldValue }) => {
          return (
            <Modal
              id="contact-modal"
              open={open}
              onClose={() => {
                resetForm();
                setChanged(false);
                handleClose();
              }}
            >
              <Box sx={ModalStyle}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: '1rem',
                  }}
                >
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    {updating ? 'Update' : 'Add'} Contact
                  </Typography>
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={() => {
                      resetForm();
                      setChanged(false);
                      handleClose();
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <Form
                  onChange={(e) => {
                    updating ? handleOnChange(e) : setChanged(true);
                  }}
                >
                  <FormObserver />
                  <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Contact Full Name"
                        name="partyName"
                        variant="standard"
                        inputProps={{ maxLength: 70 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Email"
                        name="emailAddr"
                        type="email"
                        variant="standard"
                        inputProps={{ maxLength: 40 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        variant="standard"
                        inputProps={{ maxLength: 12 }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                          setFieldValue('phone', formatPhoneNumber(e.currentTarget.value))
                        }
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Cell Phone"
                        name="cellPhone"
                        type="tel"
                        variant="standard"
                        inputProps={{ maxLength: 12 }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                          setFieldValue('cellPhone', formatPhoneNumber(e.currentTarget.value))
                        }
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Address (Optional Apt/Unit Number)"
                        name="addrStreet"
                        variant="standard"
                        inputProps={{ maxLength: 80 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="City"
                        name="addrCity"
                        variant="standard"
                        inputProps={{ maxLength: 21 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Zip Code"
                        name="addrZip"
                        variant="standard"
                        inputProps={{ maxLength: 5 }}
                        InputLabelProps={{ shrink: true }}
                        onInput={(e) => handleZipChange(e, setFieldValue)}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        component={MuiValidSelect}
                        label="State"
                        name="addrState"
                        itemValues={States}
                        itemLabels={States}
                        native
                        variant="standard"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Field
                        component={MuiValidSelect}
                        label="Contact Type"
                        name="partyType"
                        itemValues={['A', 'B', 'C', 'O']}
                        itemLabels={['Agent', 'Business', 'Contact', 'Owner']}
                        native
                        variant="standard"
                      ></Field>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: '1rem',
                    }}
                  >
                    {updating && (
                      <RemoveModal itemTitle={initialValues.partyName} onRemove={handleRemove} />
                    )}
                    <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '0.5rem' }}>
                      <Button
                        variant="outlined"
                        id="cancel-contact"
                        onClick={() => {
                          resetForm();
                          setChanged(false);
                          handleClose();
                        }}
                        sx={{ color: 'gray' }}
                      >
                        Close
                      </Button>
                      <Button
                        id="save-contact"
                        type="submit"
                        variant="outlined"
                        color="primary"
                        disabled={!changed || backdoor}
                      >
                        {isDesktop ? 'Confirm and Save Contact' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              </Box>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

export default ContactModal;
